"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createStateHistoryController = createStateHistoryController;
const react_1 = require("react");
// Utility function to get value at a path
function getPathValue(obj, path) {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
        if (key === '*') {
            // Wildcard - skip this validation for now
            return undefined;
        }
        if (current == null || typeof current !== 'object') {
            return undefined;
        }
        current = current[key];
    }
    return current;
}
// Utility function to set value at a path
function setPathValue(obj, path, value) {
    const keys = path.split('.');
    const result = JSON.parse(JSON.stringify(obj)); // Deep clone
    let current = result;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!key || key === '*') {
            // Handle wildcard by applying to all array elements
            if (Array.isArray(current)) {
                const remainingPath = keys.slice(i + 1).join('.');
                if (remainingPath) {
                    current.forEach((item, index) => {
                        current[index] = setPathValue(item, remainingPath, getPathValue(value, path));
                    });
                }
                return result;
            }
            return result; // Can't handle wildcard on non-array
        }
        if (current[key] == null) {
            current[key] = {};
        }
        current = current[key];
    }
    const lastKey = keys[keys.length - 1];
    if (lastKey && lastKey !== '*') {
        current[lastKey] = value;
    }
    return result;
}
// Create a version of the value with excluded paths filtered out
function createFilteredValue(value, excludePaths) {
    if (!excludePaths || excludePaths.length === 0) {
        return value;
    }
    let filtered = JSON.parse(JSON.stringify(value)); // Deep clone
    for (const path of excludePaths) {
        if (path.includes('*')) {
            // Handle wildcard paths
            const pathParts = path.split('.*.');
            if (pathParts.length === 2) {
                const arrayPath = pathParts[0];
                const propertyPath = pathParts[1];
                if (arrayPath && propertyPath) {
                    const arrayValue = getPathValue(filtered, arrayPath);
                    if (Array.isArray(arrayValue)) {
                        arrayValue.forEach((item, index) => {
                            const fullPath = `${arrayPath}.${index}.${propertyPath}`;
                            filtered = setPathValue(filtered, fullPath, undefined);
                        });
                    }
                }
            }
        }
        else {
            // Handle regular paths
            filtered = setPathValue(filtered, path, undefined);
        }
    }
    return filtered;
}
function useStateHistory(props, config) {
    const { maxHistory, shouldSkipHistory, excludePaths } = config;
    const { value, onUpdate } = props;
    // History stack: [past entries..., current, future entries...]
    const [history, setHistory] = (0, react_1.useState)([]);
    const [currentIndex, setCurrentIndex] = (0, react_1.useState)(-1);
    // Track the last value we set from history to prevent it from being re-added
    const lastHistoryValueRef = (0, react_1.useRef)(null);
    const previousValueRef = (0, react_1.useRef)(value);
    // Initialize history with current value if empty
    (0, react_1.useEffect)(() => {
        if (history.length === 0) {
            setHistory([value]);
            setCurrentIndex(0);
            previousValueRef.current = value;
        }
    }, [value, history.length]);
    // Add new state to history when value changes (but not from undo/redo)
    (0, react_1.useEffect)(() => {
        if (history.length === 0) {
            return; // Let initialization handle the first entry
        }
        // Skip if this value came from history (undo/redo operation)
        if (lastHistoryValueRef.current &&
            JSON.stringify(value) === JSON.stringify(lastHistoryValueRef.current)) {
            lastHistoryValueRef.current = null; // Clear the flag
            previousValueRef.current = value;
            return;
        }
        // Filter out excluded paths for comparison
        const filteredCurrent = excludePaths
            ? createFilteredValue(value, excludePaths)
            : value;
        const filteredPrevious = excludePaths
            ? createFilteredValue(previousValueRef.current, excludePaths)
            : previousValueRef.current;
        // Skip if filtered values are the same (ignoring excluded paths)
        if (JSON.stringify(filteredCurrent) === JSON.stringify(filteredPrevious)) {
            return;
        }
        // Skip if custom skip function returns true
        if (shouldSkipHistory && shouldSkipHistory(value, previousValueRef.current)) {
            return;
        }
        // Update history immediately (no debounce)
        setHistory((prevHistory) => {
            const newHistory = [...prevHistory];
            // Remove any future entries (we're creating a new branch)
            if (currentIndex < newHistory.length - 1) {
                newHistory.splice(currentIndex + 1);
            }
            // Add new entry
            newHistory.push(value);
            // Trim history if it exceeds max size
            if (newHistory.length > maxHistory) {
                newHistory.shift();
                setCurrentIndex(Math.max(0, newHistory.length - 1));
            }
            else {
                setCurrentIndex(newHistory.length - 1);
            }
            return newHistory;
        });
        previousValueRef.current = value;
    }, [value, currentIndex, maxHistory, shouldSkipHistory]);
    const undo = (0, react_1.useCallback)(() => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const previousValue = history[newIndex];
            if (previousValue !== undefined) {
                // Mark this value as coming from history
                lastHistoryValueRef.current = previousValue;
                setCurrentIndex(newIndex);
                onUpdate(previousValue);
            }
        }
    }, [currentIndex, history, onUpdate]);
    const redo = (0, react_1.useCallback)(() => {
        if (currentIndex < history.length - 1) {
            const newIndex = currentIndex + 1;
            const nextValue = history[newIndex];
            if (nextValue !== undefined) {
                // Mark this value as coming from history
                lastHistoryValueRef.current = nextValue;
                setCurrentIndex(newIndex);
                onUpdate(nextValue);
            }
        }
    }, [currentIndex, history, onUpdate]);
    const clearHistory = (0, react_1.useCallback)(() => {
        setHistory([value]);
        setCurrentIndex(0);
        previousValueRef.current = value;
    }, [value]);
    // Handle keyboard shortcuts
    (0, react_1.useEffect)(() => {
        const handleKeyDown = (event) => {
            const isMetaOrCtrl = event.metaKey || event.ctrlKey;
            if (isMetaOrCtrl && event.key === 'z') {
                event.preventDefault();
                if (event.shiftKey) {
                    // Redo: Cmd/Ctrl + Shift + Z
                    redo();
                }
                else {
                    // Undo: Cmd/Ctrl + Z
                    undo();
                }
            }
            // Alternative redo shortcut: Cmd/Ctrl + Y
            if (isMetaOrCtrl && event.key === 'y') {
                event.preventDefault();
                redo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo]);
    const canUndo = currentIndex > 0;
    const canRedo = currentIndex < history.length - 1;
    return {
        value,
        undo,
        redo,
        canUndo,
        canRedo,
        historySize: history.length,
        clearHistory,
    };
}
function createStateHistoryController(config) {
    return {
        useProps(props) {
            return useStateHistory(props, config);
        },
    };
}
//# sourceMappingURL=undoProvider.js.map